import React from 'react';
import { CheckCircle, Star, Loader2, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import T from '@/components/StyledTranslation';
import { planSavings, badgeText, fmtKr, planMonths, monthsLabel } from '@/utils/planPricing';

const promoLabels = {
  sv: 'SPARA',
  ar: 'وفّر',
  en: 'SAVE',
  tr: 'TASARRUF',
  fa: 'تخفیف',
  so: 'KEYDI',
  pl: 'OSZCZĘDŹ',
  es: 'AHORRA',
  de: 'SPARE',
};

const PricingCard = ({ 
  plan, 
  index, 
  totalPlans,
  features,
  planName,
  planDescription,
  durationText,
  onSubscribe,
  loading,
  userSubscriptionStatus,
  isCurrentPlan,
  base,
  i18n,
  t
}) => {
  const isPopular = plan.isPopular;
  const isFree = plan.price === 0;
  const isLastCard = index === totalPlans - 1;
  const lang = i18n.language || 'sv';
  // Savings vs buying the monthly plan N times (no fake "former price").
  const savings = isFree ? null : planSavings(plan, base);
  const promo = !!savings;
  const discountPercent = savings?.pct || 0;
  const saveLabel = promoLabels[lang] || promoLabels.en;
  const badge = badgeText(plan.badge, lang);
  const vsLabel = { sv: 'jämfört med', ar: 'مقارنة بـ', en: 'compared with' }[lang] || 'compared with';
  const perMonthLabel = { sv: 'kr/mån', ar: 'كرونة/شهر', en: 'kr/mo' }[lang] || 'kr/mo';
  const onDark = isLastCard || isPopular;

  const displayName = isFree 
    ? t('pricing.free') 
    : planMonths(plan) > 0 ? monthsLabel(planMonths(plan), lang)
    : ((plan.duration_value === 6 || plan.duration === 6) && (plan.duration_type === 'months' || plan.durationType === 'months')
      ? t('ui.sixMonths')
      : planName?.split(' ')[0] || planName);

  return (
    <div
      className={`relative rounded-none sm:rounded-3xl px-2 py-4 sm:p-6 transition-all duration-300 border-b-2 border-r-2 sm:border-0 overflow-hidden h-full flex flex-col hover:scale-[1.02] hover:shadow-2xl ${
        isLastCard
          ? 'bg-gradient-to-br from-indigo-400 via-purple-400 to-pink-400 dark:from-indigo-950 dark:via-purple-950 dark:to-pink-950 shadow-xl shadow-indigo-300/30 dark:shadow-indigo-900/20 dark:border-purple-800/40'
          : isPopular
          ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 dark:from-emerald-950 dark:to-emerald-900 shadow-xl shadow-emerald-500/30 dark:shadow-emerald-900/20 dark:border-emerald-800/40'
          : isFree
          ? 'bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
          : 'bg-white dark:bg-slate-800 border-2 border-emerald-200 dark:border-emerald-800/40 hover:border-emerald-400 dark:hover:border-emerald-700'
      }`}
      data-testid={`plan-${plan.id}`}
    >
      {/* Promo Ribbon */}
      {promo && (
        <div className="absolute -top-0 -right-0 sm:-top-0 sm:-right-0 z-10">
          <div className={`flex items-center gap-1 px-2 py-0.5 sm:px-3 sm:py-1 rounded-bl-xl rounded-tr-none sm:rounded-tr-3xl text-[8px] sm:text-xs font-bold shadow-lg ${
            'bg-rose-600 text-white'
          }`} data-testid={`plan-pct-${plan.id}`}>
            <Tag className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
            -{discountPercent}%
          </div>
        </div>
      )}

      {/* Badge Area */}
      <div className="h-6 sm:h-8 flex items-center justify-center mb-1 sm:mb-2">
        {(isPopular || badge) && !isCurrentPlan && (
          <div className="bg-amber-400 text-slate-900 text-[7px] sm:text-xs font-bold px-2 py-1 sm:px-3 sm:py-1.5 rounded-full flex items-center justify-center gap-0.5 sm:gap-1 shadow-md">
            <Star className="w-2 h-2 sm:w-4 sm:h-4 fill-slate-900" />
            <span>{badge || t('pricing.popular')}</span>
          </div>
        )}
        {isCurrentPlan && (
          <div className="bg-emerald-500 text-white text-[7px] sm:text-xs font-bold px-2 py-1 sm:px-3 sm:py-1.5 rounded-full flex items-center justify-center gap-0.5 sm:gap-1 shadow-md">
            <CheckCircle className="w-2 h-2 sm:w-4 sm:h-4" />
            <span>{t('ui.currentPlan')}</span>
          </div>
        )}
      </div>

      {/* Plan Details */}
      <div className="text-center mb-3 sm:mb-6">
        <div className="h-6 sm:h-8 flex items-center justify-center">
          <h3 className={`text-[10px] sm:text-xl font-bold ${isLastCard || isPopular ? 'text-white dark:text-slate-100' : 'text-slate-900 dark:text-slate-100'}`}>
            {displayName}
          </h3>
        </div>
        
        <div className="flex flex-col items-center justify-center my-2">
          {/* Old price - crossed out */}
          {promo && (
            <span dir="ltr" className={`text-xs sm:text-lg line-through decoration-2 font-semibold ${
              onDark ? 'text-white/80 decoration-rose-300' : 'text-slate-500 decoration-rose-500'
            }`} data-testid={`plan-compare-${plan.id}`}>
              {fmtKr(savings.compare)} kr
            </span>
          )}
          {/* Current price */}
          <div className="flex items-baseline justify-center gap-0.5">
            <span className={`text-xl sm:text-5xl font-black ${isLastCard || isPopular ? 'text-white dark:text-slate-100' : 'text-slate-900 dark:text-slate-100'}`}>
              {isFree ? '0' : fmtKr(plan.price)}
            </span>
            <span className={`text-sm sm:text-2xl font-bold ml-1 ${isLastCard ? 'text-purple-200 dark:text-purple-400' : isPopular ? 'text-emerald-200 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`}>
              {plan.currency === 'sek' ? 'kr' : (plan.currency || 'kr')}
            </span>
          </div>
          {/* Save badge */}
          {promo && (
            <div className={`mt-1.5 inline-flex items-center gap-1 px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-[8px] sm:text-sm font-extrabold shadow-sm ${
              onDark ? 'bg-amber-300 text-slate-900' : 'bg-rose-600 text-white'
            }`} data-testid={`plan-save-${plan.id}`}>
              {saveLabel} <bdi dir="ltr">{fmtKr(savings.save)} kr</bdi>
            </div>
          )}
          {promo && (
            <span className={`mt-1 text-[7px] sm:text-xs font-medium leading-tight ${onDark ? 'text-white/85' : 'text-slate-500'}`}>
              <bdi dir="ltr">{fmtKr(savings.perMonth)}</bdi> {perMonthLabel} · {vsLabel} <bdi dir="ltr">{savings.months} × {fmtKr(base)} kr</bdi>
            </span>
          )}
        </div>
        
        <p className={`text-[8px] sm:text-xs font-medium ${isLastCard ? 'text-purple-200 dark:text-purple-400' : isPopular ? 'text-emerald-200 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'}`}>
          {durationText || t('ui.perMonth')}
        </p>
      </div>

      {/* Features List */}
      <ul className="space-y-1 sm:space-y-3 mb-3 sm:mb-6 overflow-hidden flex-grow">
        {features && features.map((feature, idx) => (
          <li key={idx} className={`flex items-start gap-0.5 sm:gap-2 text-[7px] sm:text-sm leading-tight ${isLastCard ? 'text-white dark:text-slate-200' : isPopular ? 'text-emerald-50 dark:text-emerald-200' : 'text-slate-600 dark:text-slate-300'}`}>
            <CheckCircle className={`w-2 h-2 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5 ${isLastCard ? 'text-purple-200 dark:text-purple-400' : isPopular ? 'text-emerald-200 dark:text-emerald-400' : 'text-emerald-500 dark:text-emerald-400'}`} />
            <span className="line-clamp-2 sm:line-clamp-none">{feature}</span>
          </li>
        ))}
      </ul>

      {/* Subscribe Button */}
      <Button
        onClick={() => !isCurrentPlan && onSubscribe(plan)}
        disabled={isCurrentPlan || (!isFree && loading === plan.id)}
        className={`w-full rounded-full py-2.5 sm:py-5 text-[9px] sm:text-base font-bold active:scale-95 transition-transform mt-auto ${
          isCurrentPlan
            ? 'bg-slate-300 dark:bg-slate-600 text-slate-500 dark:text-slate-400 cursor-not-allowed'
            : isLastCard
            ? 'bg-white dark:bg-purple-800/60 text-purple-600 dark:text-purple-200 hover:bg-purple-50 dark:hover:bg-purple-800/80'
            : isPopular
            ? 'bg-white dark:bg-emerald-800/60 text-emerald-600 dark:text-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-800/80'
            : isFree
            ? 'bg-slate-900 dark:bg-slate-600 hover:bg-slate-800 dark:hover:bg-slate-500 text-white'
            : 'bg-emerald-500 dark:bg-emerald-700 hover:bg-emerald-600 dark:hover:bg-emerald-600 text-white'
        }`}
        data-testid={`subscribe-${plan.id}-btn`}
      >
        {loading === plan.id ? (
          <><Loader2 className="w-4 h-4 sm:w-5 sm:h-5 me-2 animate-spin" /> {i18n.language === 'ar' ? 'جاري التوجيه للدفع...' : i18n.language === 'sv' ? 'Omdirigerar till betalning...' : 'Redirecting to payment...'}</>
        ) : isCurrentPlan ? (
          t('ui.currentPlan')
        ) : isFree ? (
          <T k="pricing.startFree" />
        ) : (
          <T k="pricing.subscribe" />
        )}
      </Button>
    </div>
  );
};

export default PricingCard;
